import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Database, Eye, KeyRound, Pencil, Plus, Power, Trash2, Users, Send, Clock, Copy, RefreshCw,
} from 'lucide-react'
import { Breadcrumb } from '../../components/Breadcrumb'
import { FloatingXScrollbar } from '../../components/table/FloatingXScrollbar'
import { SortableHeader } from '../../components/ui/SortableHeader'
import { Pagination } from '../../components/ui/Pagination'
import { FilterRow } from '../../components/filters/FilterRow'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { useSortable } from '../../lib/use-sortable'
import { extractApiError } from '../../lib/api'
import { formatDateTime, formatNumber } from '../../lib/format'
import { copyText } from '../../lib/clipboard'
import { softwareWatchApi, isKeyActive } from '../../lib/software-watch-api'
import { ConsumerFormDialog } from '../../components/software-watch/ConsumerFormDialog'
import { ConsumerDetailsDrawer } from '../../components/software-watch/ConsumerDetailsDrawer'
import { RevealKeyDialog } from '../../components/software-watch/RevealKeyDialog'
import { usePageSize } from '../../lib/display-prefs'

// ── KPI card (local pattern, mirrors PlansPage/TenantsPage) ────────────
const KPI_TONES = {
  violet: { tile: 'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10', halo: 'from-[#EDE9FE] to-transparent dark:from-transparent' },
  emerald: { tile: 'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10', halo: 'from-[#D1FAE5] to-transparent dark:from-transparent' },
  amber: { tile: 'bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA] dark:bg-white/[0.06] dark:text-orange-300 dark:border-white/10', halo: 'from-[#FFEDD5] to-transparent dark:from-transparent' },
  sky: { tile: 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10', halo: 'from-[#DBEAFE] to-transparent dark:from-transparent' },
  rose: { tile: 'bg-[#FFE4E6] text-[#E11D48] border border-[#FECDD3] dark:bg-white/[0.06] dark:text-rose-300 dark:border-white/10', halo: 'from-[#FFE4E6] to-transparent dark:from-transparent' },
}

function KpiCard({ label, value, display, icon: Icon, tone = 'violet', loading }) {
  const tones = KPI_TONES[tone] ?? KPI_TONES.violet
  return (
    <div className="flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 min-h-[120px]">
      <div aria-hidden="true" className={'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-70 bg-gradient-to-br ' + tones.halo} />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0">{label}</h3>
          {loading ? (
            <div className="mt-3 h-7 w-14 rounded animate-pulse bg-[color:var(--color-rule)]" />
          ) : (
            <p className="mt-2 font-display text-2xl text-[color:var(--color-ink-strong)] tabular-nums leading-tight">
              {display != null ? display : formatNumber(Number(value ?? 0))}
            </p>
          )}
        </div>
        <div className={'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' + tones.tile}>
          <Icon size={24} />
        </div>
      </div>
    </div>
  )
}

const STATUS_STYLE = {
  Active: 'bg-[color:var(--color-success)] text-white dark:bg-white/[0.06] dark:text-emerald-300',
  Inactive: 'bg-[color:var(--color-ink-subtle)] text-white dark:bg-white/[0.06] dark:text-[color:var(--color-ink-muted)]',
}

function Th({ children, className = '' }) {
  return <th className={`px-4 py-3 text-left font-semibold whitespace-nowrap ${className}`}>{children}</th>
}
function Td({ children, className = '', colSpan }) {
  return <td colSpan={colSpan} className={`px-4 py-3 align-top ${className}`}>{children}</td>
}

// Consumer-level derived summaries.
function keyCounts(consumer, now) {
  const keys = consumer.keys ?? []
  const active = keys.filter((k) => isKeyActive(k, now)).length
  return { total: keys.length, active, disabled: keys.length - active }
}
function consumerScopes(consumer) {
  const set = new Set()
  for (const k of consumer.keys ?? []) for (const s of k.scopes ?? []) set.add(s)
  return [...set]
}
function consumerLastUsed(consumer) {
  let latest = null
  for (const k of consumer.keys ?? []) {
    if (k.lastUsedAt && (!latest || new Date(k.lastUsedAt) > new Date(latest))) latest = k.lastUsedAt
  }
  return latest
}

/** Copy a masked key IDENTIFIER (prefix…last4) — NOT the secret. The real key
 *  is revealed exactly once at creation and stored only as a hash, so it can
 *  never be retrieved again; this is just for referencing the key in logs. */
async function copyKeyId(text) {
  // Shared helper: falls back to a gesture-based execCommand copy when the
  // async Clipboard API is unavailable or refused. See lib/clipboard.js.
  if (!(await copyText(text))) {
    toast.error('Could not copy. Please copy it manually.')
    return
  }
  toast.success('Key ID copied to clipboard.')
}

export function SoftwareWatchPage() {
  // Scroll container for the consumers table — drives the floating
  // horizontal scrollbar pinned to the viewport bottom.
  const tableScrollRef = useRef(null)
  const qc = useQueryClient()
  // `new Date()` (not `Date.now()`) matches the repo's in-render time pattern
  // and the lint purity rule; `.getTime()` keeps a numeric millisecond value.
  const now = new Date().getTime()

  const consumersQuery = useQuery({
    queryKey: ['sw-consumers'],
    queryFn: softwareWatchApi.listConsumers,
  })
  const rawRows = consumersQuery.data?.data ?? []

  // Dialog state
  const [formTarget, setFormTarget] = useState(null) // 'new' | consumer | null
  const [detailsId, setDetailsId] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [rotateTarget, setRotateTarget] = useState(null)
  const [reveal, setReveal] = useState(null) // { raw, scopeless } | null

  // Filters / sort / pagination
  const [search, setSearch] = useState('')
  const hasAnyFilter = search.trim() !== ''
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rawRows
    return rawRows.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        consumerScopes(c).some((s) => s.toLowerCase().includes(q)) ||
        (c.keys ?? []).some((k) => (k.label ?? '').toLowerCase().includes(q)),
    )
  }, [rawRows, search])

  const { sortedRows: rows, field: sortField, direction: sortDirection, setSort } = useSortable(
    filteredRows,
    { field: 'name', direction: 'asc' },
    {
      name: (c) => (c.name ?? '').toLowerCase(),
      status: (c) => (c.enabled ? 1 : 0),
      keys: (c) => (c.keys ?? []).length,
      createdAt: (c) => (c.createdAt ? new Date(c.createdAt) : null),
      lastUsed: (c) => { const v = consumerLastUsed(c); return v ? new Date(v) : null },
    },
  )

  const [page, setPage] = useState(1)
  // Starts at (and follows) the account rows-per-page (My Preferences);
  // this list pages in the browser, so every standard size works.
  const [pageSize, setPageSize] = usePageSize()
  useEffect(() => { setPage(1) }, [sortField, sortDirection, rawRows.length, pageSize, search])
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize))
  const safePage = Math.min(page, totalPages)
  const pageRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize)

  // KPIs (computed from the consumers + their keys)
  const kpi = useMemo(() => {
    let totalRequests = 0, lastSync = null, activePlatforms = 0, activeKeys = 0
    for (const c of rawRows) {
      // A data platform's status IS its `enabled` flag — the SAME value the
      // list's Status column shows. Count platforms by that so the Active /
      // Disabled cards reconcile with the rows (Total = Active + Disabled).
      // Counting key RECORDS instead made "Disabled" read 0 against 3 disabled
      // rows, because a disabled platform can still hold a not-yet-revoked key.
      if (c.enabled) activePlatforms += 1
      for (const k of c.keys ?? []) {
        // Active KEYS stays key-level (not-yet-revoked/expired records) — it's a
        // distinct signal from platform status, so it's shown on its own card.
        if (isKeyActive(k, now)) activeKeys += 1
        totalRequests += k.usageCount ?? 0
        if (k.lastUsedAt && (!lastSync || new Date(k.lastUsedAt) > new Date(lastSync))) lastSync = k.lastUsedAt
      }
    }
    return {
      consumers: rawRows.length,
      activePlatforms,
      disabledPlatforms: rawRows.length - activePlatforms,
      activeKeys,
      totalRequests,
      lastSync,
    }
  }, [rawRows, now])

  // Consumer enable/disable + delete
  const toggleConsumer = useMutation({
    mutationFn: (c) => softwareWatchApi.updateConsumer(c._id, { enabled: !c.enabled }),
    onSuccess: (_r, c) => {
      void qc.invalidateQueries({ queryKey: ['sw-consumers'] })
      toast.success(c.enabled ? 'Data platform disabled successfully.' : 'Data platform enabled successfully.')
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })
  const removeConsumer = useMutation({
    mutationFn: (id) => softwareWatchApi.deleteConsumer(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sw-consumers'] })
      setDeleteTarget(null)
      toast.success('Data platform deleted successfully.')
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // Rotate = mint a fresh key with the SAME config, reveal it once, then revoke
  // the old one. The secure way to re-obtain a working key — the original is
  // never stored (only its hash), so it can't be shown again.
  const rotateKey = useMutation({
    mutationFn: async (c) => {
      const keys = c.keys ?? []
      const oldKey = keys.find((k) => !k.revokedAt) ?? keys[0]
      if (!oldKey) throw new Error('This platform has no key to regenerate.')
      const res = await softwareWatchApi.addKey(c._id, {
        label: oldKey.label || c.name,
        scopes: oldKey.scopes ?? [],
        allowedIps: oldKey.allowedIps ?? [],
        expiresAt: oldKey.expiresAt ?? null,
      })
      await softwareWatchApi.revokeKey(c._id, oldKey._id)
      return { raw: res.raw, scopeless: (oldKey.scopes?.length ?? 0) === 0 }
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['sw-consumers'] })
      setRotateTarget(null)
      setReveal({ raw: result.raw, scopeless: result.scopeless })
      toast.success('Key regenerated — the previous key is revoked.')
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const loading = consumersQuery.isLoading

  return (
    <div>
      <Breadcrumb current="Data Platforms" homePath="/super-admin/dashboard" />

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <Database size={22} className="text-[color:var(--color-accent-strong)]" />
            Data Platforms
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Manage the data platforms and scoped API keys that pull this
            product’s data.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setFormTarget('new')}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer"
        >
          <Plus size={14} /> Add Data Platform
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <KpiCard label="Total Data Platforms" value={kpi.consumers} icon={Users} tone="violet" loading={loading} />
        <KpiCard label="Active Platforms" value={kpi.activePlatforms} icon={Power} tone="emerald" loading={loading} />
        <KpiCard label="Disabled Platforms" value={kpi.disabledPlatforms} icon={Power} tone="rose" loading={loading} />
        <KpiCard label="Active Keys" value={kpi.activeKeys} icon={KeyRound} tone="sky" loading={loading} />
        <KpiCard label="Total Requests" value={kpi.totalRequests} icon={Send} tone="sky" loading={loading} />
        <KpiCard label="Last Sync" display={kpi.lastSync ? formatDateTime(kpi.lastSync) : '—'} icon={Clock} tone="amber" loading={loading} />
      </div>

      <FilterRow>
        <FilterRow.Search value={search} onChange={setSearch} placeholder="Search Name, Scope or Key label" />
        <FilterRow.Spacer />
        {/* Search-only filter row: the search has its own ✕ clear icon, so a
            separate "Clear all filters" would be redundant (search-only exempt,
            per the shared FilterRow convention). */}
        <FilterRow.Refresh onClick={() => consumersQuery.refetch()} loading={consumersQuery.isFetching} />
      </FilterRow>

      <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
        {/* `data-fd-xscroll` opts this table into the floating horizontal
         *  scrollbar below — matching DataTable's affordance. Additive only. */}
        <div ref={tableScrollRef} data-fd-xscroll className="overflow-x-auto">
          <table className="w-full text-left text-sm min-w-[900px] lg:min-w-0">
            <thead className="bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)]">
              <tr>
                <Th className="w-14">SR. No.</Th>
                <Th><SortableHeader field="name" label="Name" sortField={sortField} direction={sortDirection} onSort={setSort} /></Th>
                <Th><SortableHeader field="status" label="Status" sortField={sortField} direction={sortDirection} onSort={setSort} /></Th>
                <Th><SortableHeader field="keys" label="Keys" sortField={sortField} direction={sortDirection} onSort={setSort} /></Th>
                <Th>Scopes</Th>
                <Th><SortableHeader field="createdAt" label="Created At" sortField={sortField} direction={sortDirection} onSort={setSort} /></Th>
                <Th><SortableHeader field="lastUsed" label="Last Used" sortField={sortField} direction={sortDirection} onSort={setSort} /></Th>
                <Th className="w-40">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--color-rule)] text-[color:var(--color-ink)]">
              {loading ? (
                <tr><Td colSpan={8} className="text-center text-[color:var(--color-ink-muted)] py-8">Loading data platforms…</Td></tr>
              ) : consumersQuery.isError ? (
                <tr><Td colSpan={8} className="text-center text-[color:var(--color-priority-urgent)] py-8">{extractApiError(consumersQuery.error).message}</Td></tr>
              ) : rows.length === 0 ? (
                <tr><Td colSpan={8} className="text-center text-[color:var(--color-ink-muted)] py-10">{hasAnyFilter ? 'No data platforms match your search.' : 'No data platforms yet. Add one to issue API keys.'}</Td></tr>
              ) : (
                pageRows.map((c, idx) => {
                  const counts = keyCounts(c, now)
                  const scopes = consumerScopes(c)
                  const lastUsed = consumerLastUsed(c)
                  const primaryKey = (c.keys ?? []).find((k) => !k.revokedAt) ?? (c.keys ?? [])[0] ?? null
                  return (
                    <tr key={c._id} className="hover:bg-[color:var(--color-card-hover)]">
                      <Td className="text-[color:var(--color-ink-muted)]">{(safePage - 1) * pageSize + idx + 1}</Td>
                      {/* Name is free text up to 200 chars (`SwConsumer.name`
                       *  maxlength; the admin route has no DTO cap), so it
                       *  must wrap. `whitespace-nowrap` here forced the whole
                       *  name onto one line, stretching the table and pushing
                       *  every later column behind the horizontal scrollbar
                       *  (QA, 2026-09-01).
                       *
                       *  The inner block carries the width because `max-width`
                       *  on a `<td>` is unreliable under auto table-layout.
                       *  `break-words` handles BOTH shapes seen in the data: a
                       *  prose name wraps at spaces, and an unbroken token
                       *  ("TestTestTest…") is broken rather than overflowing.
                       *  280px matches the Scopes column below. */}
                      <Td className="font-medium text-[color:var(--color-ink-strong)]">
                        <div className="max-w-[280px] break-words">{c.name}</div>
                      </Td>
                      <Td>
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${c.enabled ? STATUS_STYLE.Active : STATUS_STYLE.Inactive}`}>
                          {c.enabled ? 'Active' : 'Inactive'}
                        </span>
                      </Td>
                      <Td className="whitespace-nowrap">
                        <div className="text-[color:var(--color-ink)]">
                          {counts.total}
                          {counts.total > 0 ? <span className="text-[color:var(--color-ink-muted)]"> ({counts.active} active)</span> : null}
                        </div>
                        {primaryKey ? (
                          <div className="mt-0.5 flex items-center gap-1 text-xs text-[color:var(--color-ink-muted)]">
                            <span className="font-mono">{primaryKey.keyPrefix || '—'}…{primaryKey.last4 || '—'}</span>
                            <button
                              type="button"
                              title="Copy key ID (masked — not the secret)"
                              aria-label="Copy key ID"
                              onClick={() => copyKeyId(`${primaryKey.keyPrefix || ''}…${primaryKey.last4 || ''}`)}
                              className="p-0.5 rounded text-[color:var(--color-ink-subtle)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-canvas)] cursor-pointer transition-colors"
                            >
                              <Copy size={12} strokeWidth={1.6} />
                            </button>
                            {counts.total > 1 ? <span className="font-sans"> +{counts.total - 1} more</span> : null}
                          </div>
                        ) : null}
                      </Td>
                      <Td className="max-w-[280px]">
                        {scopes.length === 0 ? (
                          <span className="text-[color:var(--color-ink-muted)]">none</span>
                        ) : scopes.includes('*') ? (
                          <span className="inline-flex rounded bg-[color:var(--color-accent-bg)] border border-[color:var(--color-accent-border)] px-1.5 py-0.5 text-xs font-medium text-[color:var(--color-accent-strong)]">All resources</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {scopes.map((s) => (
                              <span key={s} className="inline-flex rounded bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)] px-1.5 py-0.5 text-xs font-mono">
                                {s}
                              </span>
                            ))}
                          </div>
                        )}
                      </Td>
                      <Td className="whitespace-nowrap text-[color:var(--color-ink-muted)]">{formatDateTime(c.createdAt)}</Td>
                      <Td className="whitespace-nowrap text-[color:var(--color-ink-muted)]">{lastUsed ? formatDateTime(lastUsed) : '—'}</Td>
                      <Td>
                        <div className="flex items-center gap-1">
                          <button type="button" title="View" aria-label="View data platform" onClick={() => setDetailsId(c._id)} className="p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-canvas)] cursor-pointer transition-colors"><Eye size={16} strokeWidth={1.6} /></button>
                          <button type="button" title="Edit" aria-label="Edit data platform" onClick={() => setFormTarget(c)} className="p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-canvas)] cursor-pointer transition-colors"><Pencil size={16} strokeWidth={1.6} /></button>
                          <button type="button" title="Regenerate key" aria-label="Regenerate key" disabled={rotateKey.isPending} onClick={() => setRotateTarget(c)} className="p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-accent-strong)] hover:bg-[color:var(--color-canvas)] cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><RefreshCw size={16} strokeWidth={1.6} /></button>
                          <button type="button" title={c.enabled ? 'Disable' : 'Enable'} aria-label={c.enabled ? 'Disable data platform' : 'Enable data platform'} disabled={toggleConsumer.isPending} onClick={() => toggleConsumer.mutate(c)} className="p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-canvas)] cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><Power size={16} strokeWidth={1.6} /></button>
                          <button type="button" title="Delete" aria-label="Delete data platform" onClick={() => setDeleteTarget(c)} className="p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-canvas)] cursor-pointer transition-colors"><Trash2 size={16} strokeWidth={1.6} /></button>
                        </div>
                      </Td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
        {rows.length > 0 ? (
          <Pagination page={safePage} totalPages={totalPages} total={rows.length} limit={pageSize} onPageChange={setPage} onLimitChange={setPageSize} />
        ) : null}
      </div>
      <FloatingXScrollbar containerRef={tableScrollRef} />

      {/* Dialogs */}
      {formTarget !== null ? (
        <ConsumerFormDialog
          initial={formTarget === 'new' ? null : formTarget}
          onClose={() => setFormTarget(null)}
          onCreated={(raw, scopeless) => setReveal({ raw, scopeless })}
        />
      ) : null}

      {detailsId ? (
        <ConsumerDetailsDrawer
          consumerId={detailsId}
          onClose={() => setDetailsId(null)}
        />
      ) : null}

      <RevealKeyDialog
        open={reveal !== null}
        rawKey={reveal?.raw ?? ''}
        scopeless={reveal?.scopeless ?? false}
        onClose={() => setReveal(null)}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete Data Platform"
        message={deleteTarget ? `Delete “${deleteTarget.name}”? This permanently removes the data platform and REVOKES all of its API keys — any sync using them stops immediately. This cannot be undone.` : ''}
        confirmLabel="Delete Data Platform"
        variant="danger"
        confirming={removeConsumer.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => { if (deleteTarget) removeConsumer.mutate(deleteTarget._id) }}
      />

      <ConfirmDialog
        open={rotateTarget !== null}
        title="Regenerate API Key"
        message={rotateTarget ? `Generate a new key for “${rotateTarget.name}” and revoke the current one? The new key is shown once so you can copy it — any integration using the old key stops working until you update it.` : ''}
        confirmLabel="Regenerate Key"
        confirming={rotateKey.isPending}
        onCancel={() => setRotateTarget(null)}
        onConfirm={() => { if (rotateTarget) rotateKey.mutate(rotateTarget) }}
      />
    </div>
  )
}
